import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Flex,
  Input,
  InputGroup,
  InputLeftElement,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
  MenuList,
  Portal,
  Progress,
  Spinner,
  Text,
  Tooltip,
} from '@chakra-ui/react';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useDebounceCallback } from 'usehooks-ts';
import { toast } from 'sonner';
import {
  BookOpen,
  Buildings,
  CaretDown,
  CheckCircle,
  Calculator,
  Clock,
  FileText,
  MagnifyingGlass,
  Question,
  WarningCircle,
} from 'phosphor-react';
import { useMonthFilter } from '../../../../hooks/useMonthFilter';
import { MonthNavigator } from '../../../../components/shared/MonthNavigator';
import { ReturnButton } from '../../../../components/buttons/ReturnButton';
import { GlassPageBg } from '../../../../components/glass/GlassPageBg';
import { GlassCard } from '../../../../components/glass/GlassCard';
import { Tour, TourPasso, useTour } from '../../../../components/Tour';
import { useAuth } from '../../../../providers/auth';
import { AcaoLote, IStatusLote, TELA_FATURAS_PATH, TELA_PATH, mensagemErro, snApi } from './api';
import { ApuracaoCard } from './ApuracaoCard';
import { BaixarDocumentosModal } from './BaixarDocumentosModal';
import { ApuracaoDrawer } from './ApuracaoDrawer';
import { MotorCalculoDrawer } from './MotorCalculoDrawer';
import { PainelKpis } from './PainelKpis';
import { IrParaEmpresas, PainelCarteira } from './PainelCarteira';

const TAMANHO = 50;

const ACOES_LOTE: { acao: AcaoLote; rotulo: string; dica: string; transmite?: boolean }[] = [
  { acao: 'recalcular', rotulo: 'Recalcular', dica: 'Refaz o cálculo com as notas de agora' },
  {
    acao: 'aprovar',
    rotulo: 'Aprovar prontas',
    dica: 'Recalcula e aprova as que não têm nenhuma pendência aberta (só as que você pode aprovar)',
  },
  {
    acao: 'rascunho',
    rotulo: 'Rascunho no SERPRO',
    dica: 'Pede à Receita o cálculo dela (sem transmitir) e o RBT12 dela para comparar',
  },
  {
    acao: 'importar',
    rotulo: 'Importar da Receita',
    dica: 'Lê a declaração do mês anterior (apurando 09, lê a de 08): receita dos 12 meses do RBT12. Não transmite',
  },
  {
    acao: 'buscar_rbt12',
    rotulo: 'Buscar RBT12 das faltantes',
    dica: 'Importar da Receita só nas empresas ainda sem RBT12. Mês sem faturamento não é falta e empresa aberta há menos de 12 meses usa o início de atividade. Rodar de novo continua de onde parou',
  },
  {
    acao: 'transmitir',
    rotulo: 'Transmitir aprovadas (original)',
    dica: 'Consulta a Receita antes: mês já declarado não é enviado (aí só Retificar, na empresa)',
    transmite: true,
  },
  {
    acao: 'documentos',
    rotulo: 'Gerar DAS e extrato',
    dica: 'Para as transmitidas que ainda não têm guia',
    transmite: true,
  },
];

const PASSOS: TourPasso[] = [
  {
    alvo: 'mes',
    titulo: 'Competência',
    texto: 'Escolha o mês de apuração. Abre sempre no mês anterior, que é o que vence dia 20.',
  },
  {
    alvo: 'kpis',
    titulo: 'Situação do mês',
    texto:
      'A barra mostra quantas já foram declaradas e quanto falta para o dia 20. As etapas somam o total: cada empresa está em uma só (com bloqueio → só atenção → pronta para aprovar → pronta para transmitir → transmitida → concluída). Os recortes cruzam as etapas. Cada número filtra a lista; clique de novo para limpar.',
  },
  {
    alvo: 'aviso-serpro',
    titulo: 'SERPRO e certificado',
    texto:
      'Aqui aparece se a integração com a Receita está configurada e se o certificado do escritório está no cofre. Sem isso dá para calcular e aprovar, mas não transmitir.',
  },
  {
    alvo: 'recalculo',
    titulo: 'Recálculo automático',
    texto:
      'Ligado, o sistema recalcula todas as empresas toda noite às 03:30. Desligado (padrão), só calcula quando alguém clica em Calcular ou Ações → Recalcular.',
  },
  {
    alvo: 'busca',
    titulo: 'Busca',
    texto: 'Procure por nome, CNPJ ou ID. O filtro de responsável mostra só a sua carteira fiscal.',
  },
  {
    alvo: 'lote',
    titulo: 'Ações em lote',
    texto:
      'Valem para as empresas marcadas (ou todas, se nenhuma marcada). Transmitir só pega as aprovadas: nada sai sem conferência.',
  },
  {
    alvo: 'faturas',
    titulo: 'Faturas (receita sem nota)',
    texto:
      'Receita que não tem XML (fatura, locação, recibo) é lançada lá e entra no cálculo do mês. Não esqueça antes de aprovar.',
  },
  {
    alvo: 'card',
    titulo: 'A empresa',
    texto:
      'Nosso DAS ao lado do Domínio (só referência), do rascunho da Receita e do mês anterior. Vermelho = diferença.',
  },
  {
    alvo: 'parametros',
    titulo: 'Parâmetros',
    texto:
      'ISS fixo, ICMS fora do DAS, regras por item da LC 116 e filiais. Edita na aba Simples Nacional do cadastro.',
  },
  {
    alvo: 'situacao',
    titulo: 'Conferir e aprovar',
    texto:
      'Em "Conferir" estão o cálculo, a auditoria (atenção: aceite, justificativa opcional, uma a uma ou todas de uma vez; bloqueio precisa corrigir), os documentos com correção da segregação e os arquivos. Aprovam o responsável fiscal, a gestão, a diretoria e o admin.',
  },
];

export function SupervisorSimplesPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { hasPermission } = useAuth();
  const podeTransmitir = hasPermission(TELA_PATH, 'transmitir');
  const podeFaturas = hasPermission(TELA_FATURAS_PATH, 'view');
  const tour = useTour('supervisor-simples');

  const { currentDate, handlePrevMonth, handleNextMonth, handleSelectMonth, filters, setFilters } =
    useMonthFilter({ additionalParams: { situacao: '', search: '', responsavel: '', aba: '' } });
  const competencia = currentDate.clone().startOf('month').format('YYYY-MM-DD');
  // Painel (visão da carteira, estilo orquestrador) é a aba principal
  const aba: 'painel' | 'empresas' = filters.get('aba') === 'empresas' ? 'empresas' : 'painel';
  const situacao = filters.get('situacao') ?? '';
  const search = filters.get('search') ?? '';
  const responsavel = filters.get('responsavel') ?? '';

  const [textoBusca, setTextoBusca] = useState(search);
  const [textoResp, setTextoResp] = useState(responsavel);
  const [selecionados, setSelecionados] = useState<Set<number>>(new Set());
  // ZIP dos PDFs: ids marcados (vazio = todas); null = modal fechado
  const [baixarDocs, setBaixarDocs] = useState<number[] | null>(null);
  const [aberta, setAberta] = useState<number | null>(null);
  const [manual, setManual] = useState(false);
  const [tarefa, setTarefa] = useState<{ id: string; acao: AcaoLote; total: number } | null>(null);

  const setFiltro = useCallback(
    (campo: string, valor: string) => {
      const novo = new URLSearchParams(filters);
      novo.set(campo, valor);
      novo.set('page', '1');
      setFilters(novo);
    },
    [filters, setFilters],
  );
  const buscar = useDebounceCallback((c: string, v: string) => setFiltro(c, v), 400);

  /** Troca de aba; vindo do painel já chega com o filtro aplicado. */
  const irPara = useCallback(
    (destino: 'painel' | 'empresas', f?: IrParaEmpresas) => {
      const novo = new URLSearchParams(filters);
      novo.set('aba', destino);
      if (f) {
        novo.set('situacao', f.situacao ?? '');
        novo.set('responsavel', f.responsavel ?? '');
        novo.set('search', '');
        setTextoResp(f.responsavel ?? '');
        setTextoBusca('');
      }
      novo.set('page', '1');
      setFilters(novo);
      window.scrollTo({ top: 0 });
    },
    [filters, setFilters],
  );

  useEffect(() => setSelecionados(new Set()), [competencia, situacao, search, responsavel]);

  const { data: config } = useQuery({
    queryKey: ['sn-config'],
    queryFn: snApi.config,
    staleTime: 300000,
  });
  const { data: totais, isFetching: carregandoTotais } = useQuery({
    queryKey: ['sn-totais', competencia],
    queryFn: () => snApi.totais(competencia),
  });
  const { data, isLoading, isFetching, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery({
      queryKey: ['sn-lista', competencia, situacao, search, responsavel],
      queryFn: ({ pageParam }) =>
        snApi.listar({
          competencia,
          situacao: situacao || undefined,
          search: search || undefined,
          responsavel: responsavel || undefined,
          page: pageParam,
          size: TAMANHO,
        }),
      initialPageParam: 1,
      getNextPageParam: ultima => (ultima.page < ultima.pages ? ultima.page + 1 : undefined),
      enabled: aba === 'empresas',
    });
  const itens = useMemo(() => data?.pages.flatMap(p => p.items) ?? [], [data]);
  const total = data?.pages[0]?.total ?? 0;

  const sentinela = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = sentinela.current;
    if (!el) return undefined;
    const obs = new IntersectionObserver(e => {
      if (e[0].isIntersecting && hasNextPage && !isFetchingNextPage) fetchNextPage();
    });
    obs.observe(el);
    return () => obs.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const calcular = useMutation({
    mutationFn: (empresaId: number) => snApi.calcular(empresaId, competencia),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sn-lista'] });
      qc.invalidateQueries({ queryKey: ['sn-totais'] });
      qc.invalidateQueries({ queryKey: ['sn-painel'] });
    },
    onError: (e: any) => toast.error(mensagemErro(e)),
  });

  // --- lote ---------------------------------------------------------------------------
  const lote = useMutation({
    mutationFn: (v: { acao: AcaoLote; ids: number[] }) => snApi.lote(v.acao, competencia, v.ids),
    onSuccess: (r, v) => {
      setTarefa({ id: r.task_id, acao: v.acao, total: r.total });
      toast.info(`${r.total} empresa(s) na fila.`);
    },
    onError: (e: any) => toast.error(mensagemErro(e)),
  });
  const { data: status } = useQuery<IStatusLote>({
    queryKey: ['sn-lote', tarefa?.id],
    queryFn: () => snApi.statusLote(tarefa!.id),
    enabled: !!tarefa,
    refetchInterval: q => {
      const s = q.state.data?.status;
      return s === 'completed' || s === 'failed' || s === 'not_found' ? false : 2000;
    },
  });
  const terminou = status?.status === 'completed' || status?.status === 'failed';
  useEffect(() => {
    if (!tarefa || !terminou) return;
    const res = status?.progresso?.resultados ?? [];
    const falhas = res.filter(r => !r.ok);
    if (status?.status === 'failed') toast.error(status.error || 'O lote parou com erro.');
    else if (falhas.length)
      toast.warning(`${res.length - falhas.length} ok, ${falhas.length} com erro.`);
    else toast.success(`Lote concluído: ${res.length} empresa(s).`);
    qc.invalidateQueries({ queryKey: ['sn-lista'] });
    qc.invalidateQueries({ queryKey: ['sn-totais'] });
    qc.invalidateQueries({ queryKey: ['sn-painel'] });
    qc.invalidateQueries({ queryKey: ['sn-detalhe'] });
  }, [terminou]); // eslint-disable-line react-hooks/exhaustive-deps

  const aceitarAtencoes = useMutation({
    mutationFn: (v: { ids: number[]; justificativa: string }) =>
      snApi.aceitarAtencoes(competencia, v.ids, v.justificativa),
    onSuccess: r => {
      toast.success(
        r.aceitas
          ? `${r.aceitas} pendência(s) de atenção aceita(s).`
          : 'Nenhuma pendência de atenção aberta.',
      );
      qc.invalidateQueries({ queryKey: ['sn-lista'] });
      qc.invalidateQueries({ queryKey: ['sn-totais'] });
      qc.invalidateQueries({ queryKey: ['sn-painel'] });
      qc.invalidateQueries({ queryKey: ['sn-detalhe'] });
    },
    onError: (e: any) => toast.error(mensagemErro(e)),
  });

  const pedirAceiteAtencoes = (ids: number[]) => {
    const alvo = ids.length ? `${ids.length} empresa(s) marcada(s)` : 'todas as empresas do mês';
    // eslint-disable-next-line no-alert
    const just = window.prompt(
      `Aceitar todas as pendências de ATENÇÃO de ${alvo}? (bloqueio não entra; aprovadas não mudam)

Justificativa (opcional):`,
      '',
    );
    if (just === null) return;
    aceitarAtencoes.mutate({ ids, justificativa: just });
  };

  const disparar = (acao: AcaoLote, ids: number[]) => {
    if (tarefa && !terminou) {
      toast.warning('Espere o lote atual terminar.');
      return;
    }
    if (acao === 'aprovar') {
      const alvo = ids.length ? `as prontas entre ${ids.length} marcada(s)` : 'todas as prontas';
      const msg = `Aprovar ${alvo}? Cada uma é recalculada antes; se aparecer pendência, ela fica de fora.`;
      // eslint-disable-next-line no-alert
      if (!window.confirm(msg)) return;
    }
    if (acao === 'transmitir') {
      const alvo = ids.length ? `${ids.length} empresa(s) marcada(s)` : 'todas as aprovadas';
      // transmissão é definitiva: pede confirmação
      // eslint-disable-next-line no-alert
      if (!window.confirm(`Transmitir o PGDAS-D de ${alvo}? Depois disso, só por retificação.`))
        return;
    }
    if (acao === 'importar' || acao === 'rascunho') {
      const alvo = ids.length ? `${ids.length} empresa(s) marcada(s)` : 'todas as empresas do mês';
      // cada consulta ao SERPRO é cobrada
      if (
        // eslint-disable-next-line no-alert
        !window.confirm(
          `Consultar a Receita para ${alvo}? Nada é transmitido, mas cada consulta é cobrada pelo SERPRO.`,
        )
      )
        return;
    }
    lote.mutate({ acao, ids });
  };

  const feitas = Number(status?.progresso?.feitas ?? 0);
  const totalLote = Number(status?.progresso?.total ?? tarefa?.total ?? 0);
  const nomes = useMemo(
    () => Object.fromEntries(itens.map(i => [i.empresa.id, i.empresa.nome])),
    [itens],
  );
  const falhasLote = (status?.progresso?.resultados ?? []).filter(r => !r.ok);
  // lote que parou no meio (ex.: deploy reiniciou a API): as que nem rodaram
  const rodadas = new Set((status?.progresso?.resultados ?? []).map(r => r.empresa_id));
  const restantes = terminou ? (status?.progresso?.ids ?? []).filter(id => !rodadas.has(id)) : [];

  return (
    <GlassPageBg>
      <Flex direction="column" gap={5} px={{ base: 4, md: 6 }} py={{ base: 4, md: 6 }}>
        <Flex align="center" gap={3} wrap="wrap">
          <ReturnButton previousPage="modulesHome/supervisor" />
          <Flex
            w="46px"
            h="46px"
            borderRadius="14px"
            align="center"
            justify="center"
            flex="none"
            bgGradient="linear(135deg, #1B88F5, #6366F1)"
            color="white"
            boxShadow="0 8px 20px -6px rgba(99,102,241,0.45)"
          >
            <Calculator size={24} weight="duotone" />
          </Flex>
          <Box minW={0}>
            <Text
              fontSize="11px"
              letterSpacing="0.14em"
              textTransform="uppercase"
              color="#94A3B8"
              fontWeight={600}
            >
              Supervisor › Simples Nacional
            </Text>
            <Text fontSize="22px" fontWeight={700} letterSpacing="-0.02em" color="#0F172A">
              Simples Nacional
            </Text>
          </Box>
          <Box flex={1} />
          {(carregandoTotais || isFetching) && (
            <Flex role="status" aria-live="polite" align="center" gap={2}>
              <Spinner size="sm" color="#1B88F5" />
              <Text fontSize="13px" color="#64748B">
                Carregando…
              </Text>
            </Flex>
          )}
          {config && (
            <Tooltip
              label={
                config.agendamento?.ligado
                  ? `Rotinas automáticas ligadas: ${
                      config.agendamento.ativas.join(', ') || 'nenhuma ativa'
                    }. Configure em Orquestrador › Agendamentos.`
                  : 'Nada roda sozinho. Configure em Orquestrador › Agendamentos.'
              }
              fontSize="11px"
              hasArrow
              maxW="280px"
            >
              <Button
                size="sm"
                variant="ghost"
                data-tour="recalculo"
                leftIcon={<Clock size={16} />}
                color={config.agendamento?.ligado ? '#15803D' : '#64748B'}
                onClick={() => navigate('/orquestrador/agendamentos')}
              >
                {config.agendamento?.ligado ? 'Automático: ligado' : 'Automático: desligado'}
              </Button>
            </Tooltip>
          )}
          <Button size="sm" variant="ghost" leftIcon={<Question size={16} />} onClick={tour.abrir}>
            Como funciona
          </Button>
          <Button
            size="sm"
            variant="ghost"
            leftIcon={<BookOpen size={16} />}
            onClick={() => setManual(true)}
          >
            Motor de cálculo
          </Button>
          <Box data-tour="mes">
            <MonthNavigator
              currentDate={currentDate}
              onPrevMonth={handlePrevMonth}
              onNextMonth={handleNextMonth}
              onSelectMonth={handleSelectMonth}
              compact
            />
          </Box>
        </Flex>

        {config && (
          <GlassCard intensity="strong" p="10px 14px" data-tour="aviso-serpro">
            <Flex align="center" gap={2} fontSize="13px">
              {config.pendencia ? (
                <>
                  <WarningCircle size={18} color="#F59E0B" weight="fill" />
                  <Text color="#92400E">
                    {config.pendencia} — dá para calcular e aprovar; transmitir fica bloqueado.
                  </Text>
                </>
              ) : (
                <>
                  <CheckCircle size={18} color="#22C55E" weight="fill" />
                  <Text color="#166534">
                    SERPRO ({config.ambiente}) ok · certificado {config.certificado?.titular} válido
                    até{' '}
                    {config.certificado?.valido_ate?.slice(0, 10).split('-').reverse().join('/')}
                  </Text>
                </>
              )}
            </Flex>
          </GlassCard>
        )}

        <Flex gap={2} data-tour="abas">
          {(
            [
              ['painel', 'Painel da carteira'],
              ['empresas', 'Empresas'],
            ] as const
          ).map(([chave, rotulo]) => (
            <Button
              key={chave}
              size="sm"
              borderRadius="999px"
              variant={aba === chave ? 'solid' : 'outline'}
              colorScheme="blue"
              bg={aba === chave ? undefined : 'white'}
              onClick={() => irPara(chave)}
            >
              {rotulo}
            </Button>
          ))}
        </Flex>

        {aba === 'painel' && (
          <Box minW={0} maxW="100%">
            <PainelCarteira
              competencia={competencia}
              onIr={f => irPara('empresas', f)}
              onAbrirEmpresa={setAberta}
            />
          </Box>
        )}

        {aba === 'empresas' && (
          <>
            <GlassCard intensity="strong" p="14px 16px" data-tour="kpis">
              <PainelKpis
                totais={totais}
                carregando={carregandoTotais && !totais}
                situacao={situacao}
                competencia={competencia}
                onFiltro={f => setFiltro('situacao', f)}
              />
            </GlassCard>

            <GlassCard intensity="strong" p="12px 14px" overflow="visible">
              <Flex gap={3} wrap="wrap" align="center">
                <InputGroup size="sm" w={{ base: 'full', md: '320px' }} data-tour="busca">
                  <InputLeftElement pointerEvents="none">
                    <MagnifyingGlass size={15} color="#94A3B8" />
                  </InputLeftElement>
                  <Input
                    borderRadius="10px"
                    placeholder="Nome, CNPJ ou ID"
                    value={textoBusca}
                    onChange={e => {
                      setTextoBusca(e.target.value);
                      buscar('search', e.target.value);
                    }}
                  />
                </InputGroup>
                <Input
                  size="sm"
                  borderRadius="10px"
                  w={{ base: 'full', md: '220px' }}
                  placeholder="Responsável fiscal"
                  value={textoResp}
                  onChange={e => {
                    setTextoResp(e.target.value);
                    buscar('responsavel', e.target.value);
                  }}
                />
                <Text fontSize="12px" color="#64748B" fontWeight={600}>
                  {total.toLocaleString('pt-BR')} {total === 1 ? 'empresa' : 'empresas'}
                  {selecionados.size > 0 && ` · ${selecionados.size} marcada(s)`}
                </Text>
                <Box flex={1} />
                {itens.length > 0 && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() =>
                      setSelecionados(
                        selecionados.size === itens.length
                          ? new Set()
                          : new Set(itens.map(i => i.empresa.id)),
                      )
                    }
                  >
                    {selecionados.size === itens.length ? 'Desmarcar' : 'Marcar visíveis'}
                  </Button>
                )}
                {(totais?.etapas?.pronta_aprovar ?? 0) > 0 && (
                  <Tooltip
                    label="Recalcula e aprova as empresas sem nenhuma pendência aberta. Depois é só Transmitir aprovadas."
                    fontSize="11px"
                    hasArrow
                    maxW="280px"
                  >
                    <Button
                      size="sm"
                      colorScheme="green"
                      variant="outline"
                      borderRadius="10px"
                      leftIcon={<CheckCircle size={15} />}
                      isLoading={lote.isPending && lote.variables?.acao === 'aprovar'}
                      onClick={() => disparar('aprovar', Array.from(selecionados))}
                    >
                      Aprovar prontas
                      {selecionados.size ? ' (marcadas)' : ` (${totais?.etapas?.pronta_aprovar})`}
                    </Button>
                  </Tooltip>
                )}
                {podeFaturas && (
                  <Button
                    size="sm"
                    variant="outline"
                    borderRadius="10px"
                    leftIcon={<FileText size={15} />}
                    onClick={() =>
                      navigate(
                        `${TELA_FATURAS_PATH}?date_start=${competencia}&date_end=${currentDate
                          .clone()
                          .endOf('month')
                          .format('YYYY-MM-DD')}`,
                      )
                    }
                    data-tour="faturas"
                  >
                    Faturas
                  </Button>
                )}
                <Menu>
                  <MenuButton
                    as={Button}
                    size="sm"
                    colorScheme="blue"
                    borderRadius="10px"
                    rightIcon={<CaretDown size={14} />}
                    isLoading={lote.isPending || aceitarAtencoes.isPending}
                    data-tour="lote"
                  >
                    Ações {selecionados.size ? `(${selecionados.size})` : '(todas)'}
                  </MenuButton>
                  <Portal>
                    <MenuList fontSize="13px" zIndex="popover" bg="white">
                      {ACOES_LOTE.filter(a => !a.transmite || podeTransmitir).map(a => (
                        <MenuItem
                          key={a.acao}
                          onClick={() => disparar(a.acao, Array.from(selecionados))}
                          isDisabled={
                            !['recalcular', 'aprovar'].includes(a.acao) && !!config?.pendencia
                          }
                        >
                          <Box>
                            <Text fontWeight={600}>{a.rotulo}</Text>
                            <Text fontSize="11px" color="#64748B">
                              {a.dica}
                            </Text>
                          </Box>
                        </MenuItem>
                      ))}
                      <MenuDivider />
                      <MenuItem onClick={() => setBaixarDocs(Array.from(selecionados))}>
                        <Box>
                          <Text fontWeight={600}>Baixar documentos (ZIP)</Text>
                          <Text fontSize="11px" color="#64748B">
                            Guia e extrato já gerados, todos soltos no ZIP (sem subpastas). Marca
                            como &quot;Já baixados em lote&quot;
                          </Text>
                        </Box>
                      </MenuItem>
                      <MenuItem onClick={() => pedirAceiteAtencoes(Array.from(selecionados))}>
                        <Box>
                          <Text fontWeight={600}>Aceitar pendências de atenção</Text>
                          <Text fontSize="11px" color="#64748B">
                            Uma justificativa (opcional) para todas. Bloqueio continua precisando
                            corrigir
                          </Text>
                        </Box>
                      </MenuItem>
                    </MenuList>
                  </Portal>
                </Menu>
              </Flex>
              {tarefa && (
                <Box mt={3}>
                  <Flex justify="space-between" fontSize="12px" color="#475569" mb={1}>
                    <Text>
                      {ACOES_LOTE.find(a => a.acao === tarefa.acao)?.rotulo}: {feitas}/{totalLote}
                      {terminou ? ' — terminou' : ''}
                    </Text>
                    {terminou && (
                      <Button size="xs" variant="ghost" onClick={() => setTarefa(null)}>
                        Fechar
                      </Button>
                    )}
                  </Flex>
                  <Progress
                    size="sm"
                    borderRadius="full"
                    value={totalLote ? (feitas / totalLote) * 100 : 0}
                    isIndeterminate={!terminou && !feitas}
                    colorScheme={falhasLote.length ? 'orange' : 'blue'}
                  />
                  {restantes.length > 0 && (
                    <Flex
                      mt={2}
                      p="8px 10px"
                      gap={3}
                      align="center"
                      wrap="wrap"
                      borderRadius="10px"
                      bg="#FFF7ED"
                      border="1px solid #FED7AA"
                      fontSize="12px"
                      color="#9A3412"
                    >
                      <WarningCircle size={16} weight="fill" />
                      <Text flex={1} minW="220px">
                        O lote parou antes do fim: {restantes.length} empresa(s) não rodaram (a API
                        reiniciou no meio, por exemplo num deploy). Nada foi perdido nelas.
                      </Text>
                      <Button
                        size="xs"
                        colorScheme="orange"
                        isLoading={lote.isPending}
                        onClick={() => disparar(tarefa.acao, restantes)}
                      >
                        Rodar nas {restantes.length} restantes
                      </Button>
                    </Flex>
                  )}
                  {falhasLote.length > 0 && (
                    <Box mt={2} maxH="140px" overflowY="auto" fontSize="12px" color="#B91C1C">
                      {falhasLote.map(f => (
                        <Text key={f.empresa_id}>
                          {nomes[f.empresa_id] ?? `Empresa ${f.empresa_id}`}: {f.erro}
                        </Text>
                      ))}
                    </Box>
                  )}
                </Box>
              )}
            </GlassCard>

            {isLoading && (
              <Flex direction="column" align="center" gap={3} py={12}>
                <Spinner size="lg" color="#1B88F5" thickness="3px" />
                <Text fontSize="13px" color="#64748B">
                  Carregando empresas…
                </Text>
              </Flex>
            )}
            {!isLoading && itens.length === 0 && (
              <GlassCard intensity="medium" p={10}>
                <Flex direction="column" align="center" gap={2}>
                  <Buildings size={40} color="#CBD5E1" />
                  <Text fontSize="13px" color="#64748B">
                    Nenhuma empresa encontrada.
                  </Text>
                </Flex>
              </GlassCard>
            )}
            <Flex direction="column" gap={3}>
              {itens.map((item, i) => (
                <ApuracaoCard
                  key={item.empresa.id}
                  item={item}
                  primeiro={i === 0}
                  selecionado={selecionados.has(item.empresa.id)}
                  onSelecionar={v =>
                    setSelecionados(atual => {
                      const novo = new Set(atual);
                      if (v) novo.add(item.empresa.id);
                      else novo.delete(item.empresa.id);
                      return novo;
                    })
                  }
                  onAbrir={() => setAberta(item.empresa.id)}
                  onCalcular={() => calcular.mutate(item.empresa.id)}
                  calculando={calcular.isPending && calcular.variables === item.empresa.id}
                />
              ))}
            </Flex>
            <div ref={sentinela} className="h-10 w-full" />
            {isFetchingNextPage && (
              <Flex align="center" justify="center" py={4} gap={3}>
                <Spinner size="md" color="#1B88F5" />
              </Flex>
            )}
          </>
        )}
      </Flex>

      <ApuracaoDrawer
        empresaId={aberta}
        competencia={competencia}
        onFechar={() => setAberta(null)}
        onLote={disparar}
        podeTransmitir={podeTransmitir}
      />
      <Tour passos={PASSOS} aberto={tour.aberto} onFechar={tour.fechar} />
      <MotorCalculoDrawer aberto={manual} onFechar={() => setManual(false)} />
      {baixarDocs && (
        <BaixarDocumentosModal
          competencia={competencia}
          empresaIds={baixarDocs}
          onFechar={() => setBaixarDocs(null)}
        />
      )}
    </GlassPageBg>
  );
}
